import React, { useEffect, useMemo, useState } from "react";
import { debounce } from "@mui/material";
import {
	CustomButton,
	CustomDialog,
	CustomSwitch,
	CustomTextField,
	icon,
	IconComponent,
	LimitedTextField,
	MultipleWordInput,
	Selector,
} from "../../../../../../common/components";
import styles from "./CriteriaCreation.module.css";
import { CRITERIA_TYPES } from "../../constants";
import { isEqual, isNonEmptyArray } from "../../../../../../common/utils";
import { translateEnumTypes } from "../../utils";
import { translate } from "../../../../../../common/providers";
import { useApi } from "../../../../../../common/hooks";
import { FormStudioService } from "../../../../../../api";
import AgentSidePanel from "../agent-panel/AgentSidePanel";

const debouncedFunction = debounce((func) => func(), 500);

export default function CriteriaCreation({ criteria = null, dialogType = "creation", open, onClose, onRefresh }) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [strategy, setStrategy] = useState("");
	const [examples, setExamples] = useState("");
	const [complianceGuidelines, setComplianceGuidelines] = useState("");
	const [complianceEnabled, setComplianceEnabled] = useState(false);

	const [openAgentSidePanel, setOpenAgentSidePanel] = useState(false);
	const [temporaryCriteria, setTemporaryCriteria] = useState(null);
	const [type, setType] = useState("");
	const [agent, setAgent] = useState(null);
	const [keywords, setKeywords] = useState([]);
	const [isMultipleChoiceList, setIsMultipleChoiceList] = useState(false);
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const [isDublicateName, setIsDuplicateName] = useState(false);
	const [originalName, setOriginalName] = useState("");
	const { call: createCriteria } = useApi(FormStudioService.createCriteria);
	const { call: updateCriteria } = useApi(FormStudioService.updateCriteria);
	const { call: getCriteria } = useApi(FormStudioService.getCriteria);
	const { call: duplicateCriteria } = useApi(FormStudioService.duplicateCriteria);
	const { call: getAllCriteria } = useApi(FormStudioService.getAllCriteria);
	const isValidForm = useMemo(
		() =>
			name?.length > 0 &&
			type &&
			((type === CRITERIA_TYPES.LIST.name && isNonEmptyArray(keywords)) || type !== CRITERIA_TYPES.LIST.name) &&
			(dialogType === "creation" ||
				(dialogType === "duplication" && name !== criteria.name) ||
				(dialogType === "modification" &&
					(criteria?.agentId !== agent?.agentId ||
						temporaryCriteria?.name !== name ||
						temporaryCriteria?.description !== description ||
						temporaryCriteria?.contentExamples !== examples ||
						temporaryCriteria?.complianceGuidelines !== complianceGuidelines ||
						temporaryCriteria?.complianceEnabled !== complianceEnabled ||
						temporaryCriteria?.searchStrategy !== strategy ||
						temporaryCriteria?.type !== type ||
						(temporaryCriteria?.type === CRITERIA_TYPES.LIST.name &&
							type === CRITERIA_TYPES.LIST.name &&
							(isMultipleChoiceList !== temporaryCriteria?.isMultiple ||
								!isEqual(temporaryCriteria?.possibleValues, keywords)))))),
		[
			complianceGuidelines,
			complianceEnabled,
			agent,
			name,
			dialogType,
			description,
			isMultipleChoiceList,
			examples,
			strategy,
			keywords,
			criteria,
			type,
			temporaryCriteria,
		]
	);
	useEffect(() => {
		if (criteria?.id) {
			getCriteria({ fieldId: criteria.id })
				.then((data) => {
					setTemporaryCriteria(data);
					setName(data.name);
					setOriginalName(data.name);
					setDescription(data.description);
					setStrategy(data.searchStrategy || "");
					setExamples(data.contentExamples || "");
					setComplianceEnabled(data.complianceEnabled || false);
					setComplianceGuidelines(data.complianceGuidelines || "");
					setType(data.type);
					setAgent({
						agentId: data?.agentId || criteria?.agentId,
						agentTitle: data?.agentTitle || criteria?.agentTitle,
					});
					if (data.type === CRITERIA_TYPES.LIST.name && isNonEmptyArray(data.possibleValues)) {
						setKeywords(data.possibleValues);
						setIsMultipleChoiceList(data.isMultiple);
					}
				})
				.catch(console.error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [criteria?.id]);
	const handleChangeName = (newName) => {
		if (newName && newName !== originalName) {
			debouncedFunction(() => {
				getAllCriteria({ name: newName })
					.then((data) => {
						setIsDuplicateName(data.some((item) => item.name?.toLowerCase() === newName?.toLowerCase()));
					})
					.catch(console.error);
			});
		}
		setName(newName);
	};
	const handleChangeDescription = (newDescription) => {
		setDescription(newDescription);
	};
	const handleChangeStrategy = (newValue) => {
		setStrategy(newValue);
	};
	const handleChangeExamples = (newValue) => {
		setExamples(newValue);
	};
	const handleChangeComplianceGuidelines = (newValue) => {
		setComplianceGuidelines(newValue);
	};
	const handleChangeComplianceEnabled = () => {
		setComplianceEnabled((prev) => !prev);
	};
	const handleAgent = (newValue) => {
		setAgent((newValue && { agentId: newValue?.id, agentTitle: newValue?.title }) || null);
		setOpenAgentSidePanel(false);
	};
	const handleSelectType = (newType) => {
		const { name: typeName } = newType;
		setType(typeName);
	};
	const handleChangeKeywords = (list) => {
		setKeywords(list);
	};
	const handleChangeMultipleChoice = () => {
		setIsMultipleChoiceList((prev) => !prev);
	};
	const selectorItemRenderer = (label) => (
		<span className={styles.itemRow}>
			<IconComponent color="var(--grey-800)" icon={icon[CRITERIA_TYPES?.[label]?.icon]} />
			<span>{translateEnumTypes(label)}</span>
		</span>
	);

	const resetState = () => {
		setName("");
		setDescription("");
		setType("");
		setStrategy("");
		setExamples("");
		setComplianceGuidelines("");
		setOriginalName("");
		setKeywords([]);
		setAgent(null);
		setIsMultipleChoiceList(true);
		setOpenConfirmation(false);
		setComplianceEnabled(false);
	};
	const handleDefinitiveClose = () => {
		resetState();
		onClose();
	};
	const handleClose = () => {
		if (dialogType === "creation") {
			if (!name && !type && !isNonEmptyArray(keywords)) {
				handleDefinitiveClose();
			} else {
				setOpenConfirmation(true);
			}
		} else {
			setOpenConfirmation(true);
		}
	};
	const handleRefresh = () => {
		if (onRefresh && typeof onRefresh === "function") {
			onRefresh();
		}
	};
	const handleSubmit = () => {
		if (isValidForm) {
			const payload = {
				name,
				description,
				type,
				searchStrategy: strategy,
				contentExamples: examples,
				complianceEnabled,
				agentId: agent?.agentId || null,
			};
			if (type === CRITERIA_TYPES.LIST.name) {
				payload.possibleValues = keywords;
				payload.isMultiple = isMultipleChoiceList;
			}
			if (complianceEnabled) {
				payload.complianceGuidelines = complianceGuidelines;
			}
			if (criteria) {
				if (dialogType === "modification") {
					updateCriteria({ fieldId: criteria.id }, payload)
						.then(handleRefresh)
						.catch(console.error)
						.finally(handleDefinitiveClose);
				} else {
					duplicateCriteria({ fieldId: criteria.id }).then(handleRefresh).catch(console.error);
				}
			} else {
				createCriteria(payload).then(handleRefresh).catch(console.error).finally(handleDefinitiveClose);
			}
		}
	};

	const handleOpenDialogAgentSelection = () => {
		setOpenAgentSidePanel((prev) => !prev);
	};
	const renderCustomFooter = () => (
		<div className={styles.customFooter}>
			<CustomButton color="secondary" variant="outlined" onClick={handleClose}>
				{translate("common:btn.cancel")}
			</CustomButton>
			<CustomButton variant="outlined" onClick={handleOpenDialogAgentSelection}>
				{(agent?.agentId && agent?.agentTitle) || translate("dsi.form-studio.criteria.select-agent.button")}
			</CustomButton>
			<CustomButton color="primary" disabled={!isValidForm} variant="contained" onClick={handleSubmit}>
				{translate("common:btn.submit")}
			</CustomButton>
		</div>
	);
	return (
		<>
			<CustomDialog
				disableBackdropClick
				disableEnforceFocus
				disableEscapeKeyDown
				displayCloseCross
				isForm
				className={styles.criteriaCreation}
				classNameDialog={`${styles.criteriaCreationDialog} ${
					openAgentSidePanel && styles["criteriaCreation--side-panel-open"]
				}`}
				customFooter={renderCustomFooter}
				open={open}
				Paperprops={{ sx: { width: "50vw", height: "90vh", maxWidth: "none" } }}
				submitDisabled={!isValidForm}
				submitLabel={translate("common:btn.validate")}
				title={translate(`dsi.form-studio.criteria.${dialogType}`)}
				onClose={handleClose}
				onSubmit={handleSubmit}
			>
				<div className={styles.dialog__container}>
					<div>
						<CustomTextField
							fullWidth
							placeholder={translate("common:field.name")}
							size="small"
							value={name}
							variant="outlined"
							onChange={(e) => handleChangeName(e.target.value)}
						/>
						{isDublicateName && (
							<div className={styles.warningMessage}>
								<IconComponent color="var(--amber-500)" icon={icon.faExclamationTriangle} />
								{translate("dsi.form-studio.criteria.name-warning")}
							</div>
						)}
					</div>
					<div data-has-textfield>
						<LimitedTextField
							fullWidth
							multiline
							className={styles.textField}
							maxLength={5000}
							placeholder={translate("dsi.form-studio.criteria.description")}
							showHelper={false}
							value={description}
							variant="outlined"
							onChange={handleChangeDescription}
						/>
					</div>
					<div>
						<span>{translate("dsi.form-studio.criteria.modification.search-strategy")}</span>
					</div>
					<div data-has-textfield>
						<LimitedTextField
							fullWidth
							multiline
							className={styles.textField}
							maxLength={5000}
							placeholder={translate("common:input.placeholder.start-to-write")}
							showHelper={false}
							value={strategy}
							variant="outlined"
							onChange={handleChangeStrategy}
						/>
					</div>
					<div>
						<span>{translate("dsi.form-studio.criteria.modification.contained-examples")}</span>
					</div>
					<div data-has-textfield>
						<LimitedTextField
							fullWidth
							multiline
							className={styles.textField}
							maxLength={5000}
							placeholder={translate("common:input.placeholder.start-to-write")}
							showHelper={false}
							value={examples}
							variant="outlined"
							onChange={handleChangeExamples}
						/>
					</div>
					<div>
						<Selector
							fullWidth
							field="key"
							itemRenderer={selectorItemRenderer}
							items={Object.keys(CRITERIA_TYPES).map((key) => ({ key, ...CRITERIA_TYPES[key] }))}
							label={translate("dsi.form-studio.criteria.type")}
							value={type}
							valueRenderer={selectorItemRenderer}
							onChange={(e) => handleSelectType(e.target.value)}
						/>
					</div>
					{type === CRITERIA_TYPES.LIST.name && (
						<div className={styles.itemsContainer}>
							<div>{translate("dsi.form-studio.criteria.type.list.items")}</div>
							<div>
								<CustomSwitch
									disableRipple
									checked={isMultipleChoiceList}
									onChange={handleChangeMultipleChoice}
								/>
								<span>{translate("dsi.form-studio.criteria.type.list.multiple.title")}</span>
							</div>
							<MultipleWordInput
								separator=","
								wordObjects={(isNonEmptyArray(keywords) && keywords) || []}
								onChange={handleChangeKeywords}
							/>
						</div>
					)}
					<div>
						<span>{translate("dsi.form-studio.criteria.modification.compliance-analysis")}</span>
					</div>
					<div>
						<CustomSwitch
							disableRipple
							checked={complianceEnabled}
							onChange={handleChangeComplianceEnabled}
						/>
					</div>
					<div>
						<span>{translate("dsi.form-studio.criteria.modification.compliance-status")}</span>
					</div>
					<div data-has-textfield>
						<LimitedTextField
							fullWidth
							multiline
							className={styles.textField}
							disabled={!complianceEnabled}
							maxLength={5000}
							placeholder={translate("common:input.placeholder.start-to-write")}
							showHelper={false}
							value={complianceGuidelines}
							variant="outlined"
							onChange={handleChangeComplianceGuidelines}
						/>
					</div>
				</div>
				<CustomDialog
					iconColor="var(--red-500)"
					iconModel={icon.faExclamationTriangle}
					open={openConfirmation}
					submitLabel={translate("common:btn.confirm")}
					subTitle={translate(`dsi.form-studio.criteria.cancel-${dialogType}`)}
					onClose={() => setOpenConfirmation(false)}
					onSubmit={handleDefinitiveClose}
				/>
			</CustomDialog>
			<AgentSidePanel
				agent={agent}
				criteriaId={criteria?.id}
				open={openAgentSidePanel}
				onClose={handleOpenDialogAgentSelection}
				onSubmit={handleAgent}
			/>
		</>
	);
}
